import {
    LayerModelLayerType,
    type PanelReplacementGridModel,
    type PrefabModel
} from 'api/models';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputModifier } from 'app/modules/wallforming/components/input-binding/types/inputModifier';
import { parseChord } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { PANEL_ELEMENT_TYPES } from 'shared/constants';

import type {
    Axis,
    CoverAreaBounds,
    PanelDimension,
    PanelGridItem,
    PanelReplacementGrid,
    PanelResizeDirection,
    SelectedPanel
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export type {
    CoverAreaBounds,
    PanelDimension,
    PanelGridItem,
    PanelReplacementGrid,
    PanelResizeDirection
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';

export const EMPTY_PANEL_REPLACEMENT_GRID: PanelReplacementGrid = {
    items: [],
    byWidth: new Map(),
    byHeight: new Map()
};

export const PANEL_REPLACEMENT_OVERLAY_Z_INDEX_RANGE: number[] = [16777271, 0];
export const PANEL_REPLACEMENT_LABEL_Z_INDEX_RANGE: number[] = [16777272, 16777272];

const dimensionPrecision = 10_000;
const panelGridCache = new Map<string, { data: PanelReplacementGridModel; grid: PanelReplacementGrid }>();
const axes = ['x', 'y', 'z'] as const;
const fallbackPanelCache = new WeakMap<
    PrefabModel,
    {
        articleNumber: string;
        lifeCycleCode: PanelGridItem['article']['lifeCycleCode'];
        partNamesKey: string;
        item: PanelGridItem;
    }
>();
const axisPairs: Array<[Axis, Axis]> = [
    ['x', 'y'],
    ['x', 'z'],
    ['y', 'z']
];

function roundDimension(value: number): number {
    return Math.round(value * dimensionPrecision) / dimensionPrecision;
}

function buildDimensionMap(
    mapping: Record<string, string[]>,
    items: PanelGridItem[],
    dimension: PanelDimension
): Map<number, PanelGridItem[]> {
    const itemsById = new Map(items.map((item) => [item.article.id, item]));
    const mapped = new Map(
        Object.entries(mapping).map(([size, articleIds]) => [
            Number(size),
            articleIds.flatMap((articleId) => {
                const item = itemsById.get(articleId);
                return item ? [item] : [];
            })
        ])
    );
    const mappedItemCount = [...mapped.values()].reduce((count, group) => count + group.length, 0);
    if (mappedItemCount === items.length) {
        return mapped;
    }

    return items.reduce((dimensionMap, item) => {
        const size = roundDimension(item.bounds[dimension]);
        const group = dimensionMap.get(size) ?? [];
        group.push(item);
        dimensionMap.set(size, group);
        return dimensionMap;
    }, new Map<number, PanelGridItem[]>());
}

function getPrefabBounds(prefab: PrefabModel): CoverAreaBounds | undefined {
    const coverArea = prefab.logicLayers.find((layer) => layer.layerType === LayerModelLayerType.COVERAREA);
    const pointGroups =
        coverArea?.geometries.map((geometry) => geometry.points).filter((points) => points.length > 0) ?? [];
    const groups =
        pointGroups.length > 0
            ? pointGroups
            : prefab.children.flatMap((child) => {
                  const bounds = getPrefabBounds(child);
                  return bounds
                      ? [
                            [
                                { x: bounds.x.min, y: bounds.y.min, z: bounds.z.min },
                                { x: bounds.x.max, y: bounds.y.max, z: bounds.z.max }
                            ]
                        ]
                      : [];
              });
    if (groups.length === 0) {
        return undefined;
    }

    const boundsFor = (points: Array<{ x: number; y: number; z: number }>): CoverAreaBounds | undefined => {
        const ranges = Object.fromEntries(
            axes.map((axis) => [
                axis,
                {
                    min: Math.min(...points.map((point) => point[axis])),
                    max: Math.max(...points.map((point) => point[axis]))
                }
            ])
        ) as Record<Axis, { min: number; max: number }>;
        const [widthAxis, heightAxis] = [...axisPairs].sort((left, right) => {
            const leftWidth = ranges[left[0]].max - ranges[left[0]].min;
            const leftHeight = ranges[left[1]].max - ranges[left[1]].min;
            const rightWidth = ranges[right[0]].max - ranges[right[0]].min;
            const rightHeight = ranges[right[1]].max - ranges[right[1]].min;
            return (
                rightWidth * rightHeight - leftWidth * leftHeight ||
                rightWidth - leftWidth ||
                rightHeight - leftHeight
            );
        })[0];
        const width = ranges[widthAxis].max - ranges[widthAxis].min;
        const height = ranges[heightAxis].max - ranges[heightAxis].min;
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            return undefined;
        }
        return { ...ranges, widthAxis, heightAxis, width, height };
    };

    const candidates = groups.map(boundsFor).filter((bounds): bounds is CoverAreaBounds => bounds !== undefined);
    return candidates.sort(
        (left, right) =>
            right.width * right.height - left.width * left.height ||
            right.width - left.width ||
            right.height - left.height
    )[0];
}

function getFallbackPanel(
    representation: PrefabModel,
    articleId: string,
    articleNumber: string,
    lifeCycleCode: PanelGridItem['article']['lifeCycleCode'],
    partNames: string[]
): PanelGridItem | undefined {
    const partNamesKey = JSON.stringify(partNames);
    const cached = fallbackPanelCache.get(representation);
    if (
        cached?.articleNumber === articleNumber &&
        cached.lifeCycleCode === lifeCycleCode &&
        cached.partNamesKey === partNamesKey
    ) {
        return cached.item;
    }

    const bounds = getPrefabBounds(representation);
    if (!bounds) {
        return undefined;
    }

    const item: PanelGridItem = {
        article: { id: articleId, articleNumber, lifeCycleCode },
        bounds,
        partNames
    };
    fallbackPanelCache.set(representation, { articleNumber, lifeCycleCode, partNamesKey, item });
    return item;
}

export function getPanelReplacementGrid(
    systemId: string,
    filterActive: boolean,
    data: PanelReplacementGridModel
): PanelReplacementGrid {
    const cacheKey = `${systemId}:${filterActive}`;
    const cached = panelGridCache.get(cacheKey);
    if (cached?.data === data) {
        return cached.grid;
    }

    const items: PanelGridItem[] = data.panels.map((panel) => ({
        article: {
            id: panel.articleId,
            articleNumber: panel.articleNumber,
            lifeCycleCode: panel.lifeCycleCode
        },
        bounds: panel.bounds,
        partNames: panel.partNames
    }));
    const grid = {
        items,
        byWidth: buildDimensionMap(data.byWidth, items, 'width'),
        byHeight: buildDimensionMap(data.byHeight, items, 'height')
    };
    panelGridCache.set(cacheKey, { data, grid });
    return grid;
}

export function getCachedPanelReplacementGrid(systemId: string, filterActive: boolean): PanelReplacementGrid | undefined {
    return panelGridCache.get(`${systemId}:${filterActive}`)?.grid;
}

export function getPanelDimension(item: PanelGridItem, dimension: PanelDimension, rotated90: boolean): number {
    const original = dimension === 'width' ? item.bounds.width : item.bounds.height;
    const swapped = dimension === 'width' ? item.bounds.height : item.bounds.width;
    return rotated90 ? swapped : original;
}

function getDimensionMap(
    grid: PanelReplacementGrid,
    dimension: PanelDimension,
    rotated90: boolean
): Map<number, PanelGridItem[]> {
    const usesHeight = (dimension === 'height') !== rotated90;
    return usesHeight ? grid.byHeight : grid.byWidth;
}

function hasMatchingOtherDimension(
    item: PanelGridItem,
    current: PanelGridItem,
    dimension: PanelDimension,
    rotated90: boolean
): boolean {
    const otherDimension = dimension === 'width' ? 'height' : 'width';
    return (
        roundDimension(getPanelDimension(item, otherDimension, rotated90)) ===
        roundDimension(getPanelDimension(current, otherDimension, rotated90))
    );
}

function getCompatiblePanelCandidates(
    grid: PanelReplacementGrid,
    current: PanelGridItem,
    dimension: PanelDimension,
    rotated90: boolean,
    compatiblePartCount: number
): PanelGridItem[] {
    const currentDimension = roundDimension(getPanelDimension(current, dimension, rotated90));
    return [...getDimensionMap(grid, dimension, rotated90).values()]
        .flat()
        .filter(
            (item) =>
                item.article.id !== current.article.id &&
                item.partNames.length === compatiblePartCount &&
                roundDimension(getPanelDimension(item, dimension, rotated90)) !== currentDimension
        );
}

export function getCompatiblePanels(
    grid: PanelReplacementGrid,
    current: PanelGridItem,
    dimension: PanelDimension,
    rotated90: boolean,
    compatiblePartCount: number
): PanelGridItem[] {
    return getCompatiblePanelCandidates(grid, current, dimension, rotated90, compatiblePartCount).sort(
        (left, right) =>
            Number(hasMatchingOtherDimension(right, current, dimension, rotated90)) -
                Number(hasMatchingOtherDimension(left, current, dimension, rotated90)) ||
            getPanelDimension(left, dimension, rotated90) - getPanelDimension(right, dimension, rotated90)
    );
}

export function getNextPanel(
    grid: PanelReplacementGrid,
    current: PanelGridItem,
    dimension: PanelDimension,
    direction: PanelResizeDirection,
    rotated90: boolean,
    compatiblePartCount: number
): PanelGridItem | undefined {
    const currentDimension = getPanelDimension(current, dimension, rotated90);
    return getCompatiblePanelCandidates(grid, current, dimension, rotated90, compatiblePartCount).reduce<
        PanelGridItem | undefined
    >((best, item) => {
        const itemDimension = getPanelDimension(item, dimension, rotated90);
        if (direction * (itemDimension - currentDimension) <= 0) {
            return best;
        }
        if (!best) {
            return item;
        }

        const itemMatches = hasMatchingOtherDimension(item, current, dimension, rotated90);
        const bestMatches = hasMatchingOtherDimension(best, current, dimension, rotated90);
        const bestDimension = getPanelDimension(best, dimension, rotated90);
        return Number(itemMatches) > Number(bestMatches) ||
            (itemMatches === bestMatches &&
                direction * (itemDimension - currentDimension) <
                    direction * (bestDimension - currentDimension))
            ? item
            : best;
    }, undefined);
}

export function getNearestPanel(
    grid: PanelReplacementGrid,
    current: PanelGridItem,
    dimension: PanelDimension,
    targetDimension: number,
    rotated90: boolean,
    compatiblePartCount: number
): PanelGridItem | undefined {
    const nearest = getCompatiblePanelCandidates(grid, current, dimension, rotated90, compatiblePartCount).reduce<
        PanelGridItem | undefined
    >(
        (best, item) => {
            const itemDistance = Math.abs(getPanelDimension(item, dimension, rotated90) - targetDimension);
            if (!best) {
                return item;
            }

            const bestDimension = getPanelDimension(best, dimension, rotated90);
            const bestDistance = Math.abs(bestDimension - targetDimension);
            const itemMatches = hasMatchingOtherDimension(item, current, dimension, rotated90);
            const bestMatches = hasMatchingOtherDimension(best, current, dimension, rotated90);
            return (
                itemDistance < bestDistance ||
                (itemDistance === bestDistance &&
                    (Number(itemMatches) > Number(bestMatches) ||
                        (itemMatches === bestMatches &&
                            getPanelDimension(item, dimension, rotated90) < bestDimension)))
            )
                ? item
                : best;
        },
        undefined
    );
    return nearest &&
        Math.abs(getPanelDimension(current, dimension, rotated90) - targetDimension) <
            Math.abs(getPanelDimension(nearest, dimension, rotated90) - targetDimension)
        ? current
        : (nearest ?? current);
}

export function getSelectedPanel(
    state: PlacementState,
    grid: PanelReplacementGrid
): SelectedPanel | null {
    const selectedIds = Object.keys(state.articleInstanceSelections);
    if (selectedIds.length !== 1) {
        return null;
    }

    const instanceId = selectedIds[0];
    const articleEntry = Object.entries(state.articles).find(([, article]) => article.articleInstances[instanceId]);
    const selectedArticle = articleEntry?.[1];
    const matchingItems = selectedArticle
        ? grid.items.filter((candidate) => candidate.article.articleNumber === selectedArticle.articleNumber)
        : [];
    const item =
        grid.items.find((candidate) => candidate.article.id === articleEntry?.[0]) ??
        (matchingItems.length === 1 ? matchingItems[0] : undefined) ??
        (() => {
            if (!selectedArticle || !articleEntry) {
                return undefined;
            }
            const representation =
                state.articleDragRefsMap.current
                    .get(articleEntry[0])
                    ?.articleRef.current?.articleRepresentations[0];
            if (!representation) {
                return undefined;
            }
            return getFallbackPanel(
                representation,
                articleEntry[0],
                selectedArticle.articleNumber,
                selectedArticle.lifeCycleCode,
                Object.keys(articleEntry[1].articleInstances[instanceId].parts)
            );
        })();
    const instance = articleEntry?.[1].articleInstances[instanceId];
    if (!item || !instance) {
        return null;
    }

    return { item, articleId: articleEntry[0], instanceId, instance };
}

export function canOpenPanelQuickReplacement(state: PlacementState): boolean {
    const selectedIds = Object.keys(state.articleInstanceSelections);
    if (selectedIds.length !== 1) {
        return false;
    }

    const selectedInstanceId = selectedIds[0];
    const selectedInstance = Object.values(state.articles).find(
        (article) => selectedInstanceId in article.articleInstances
    );
    return (
        selectedInstance?.elementType !== undefined &&
        PANEL_ELEMENT_TYPES.has(selectedInstance.elementType) &&
        canContinuePanelQuickReplacement(state)
    );
}

export function canContinuePanelQuickReplacement(state: PlacementState): boolean {
    return (
        Object.keys(state.articleInstanceSelections).length === 1 &&
        !state.isViewMode &&
        !state.isAutoDragActiveRef.current &&
        !state.isDraggingActive()
    );
}

export function getPanelQuickReplacementInwardModifier(state: PlacementState): InputModifier | undefined {
    const binding = state.inputBindings.slots[InputActionId.ArticleQuickReplacementInwardModifier].find(
        (chord) => chord !== null
    );
    const code = binding ? parseChord(binding)?.code : undefined;
    return Object.values(InputModifier).find((modifier) => modifier === code);
}

export function isPanelQuickReplacementInwardModifierActive(
    state: PlacementState,
    event: KeyboardEvent | MouseEvent
): boolean {
    const modifier = getPanelQuickReplacementInwardModifier(state);
    if (!modifier) {
        return false;
    }

    switch (modifier) {
        case InputModifier.Ctrl:
            return event.ctrlKey;
        case InputModifier.Shift:
            return event.shiftKey;
        case InputModifier.Alt:
            return event.altKey;
        case InputModifier.Meta:
            return event.metaKey;
    }
}

export function isRotation90Degrees(rotation: number): boolean {
    const normalized = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const tolerance = 1e-5;
    return Math.abs(normalized - Math.PI / 2) < tolerance || Math.abs(normalized - (Math.PI * 1.5)) < tolerance;
}

export function getBoundsAxis(bounds: CoverAreaBounds, axis: Axis) {
    return bounds[axis];
}
